import * as turf from "@turf/turf";
import queryString from "query-string";
import * as d3 from "d3";
import { DEFAULT_LOCATION } from "../constants/constants";

const query = queryString.parse(window.location.href.split("?")[1] || "");
const dataDir = `${process.env.VUE_APP_STATIC_PATH}/data`;

const MAXIMUM_LOCATION = 1;
const MINIMUM_LOCATION = 0;
const TICK_SIZE = 0.01;
const DECAY = 0.95;
const ACCELERATION_SPEED = 0.00005;
const VELOCITY_LIMIT = 0.1;

let lastTick;
const MS_PER_FRAME = 16.6;

const getCorrectedLocation = location => {
  if (isNaN(location)) return MINIMUM_LOCATION;
  if (location < MINIMUM_LOCATION) return MINIMUM_LOCATION;
  if (location > MAXIMUM_LOCATION) return MAXIMUM_LOCATION;
  return location;
};

const geolocation = {
  state: {
    location: getCorrectedLocation(+query.d) || DEFAULT_LOCATION,
    velocity: 0,
    direction: -1,
    acceleration: 0,
    sunset: false,
    flipped: query.flip === "true" || false,
    jumping: false,
    searchResults: [],
    needsToDemoFlip: true,
    intersections: []
  },

  mutations: {
    tick(state, { timestamp }) {
      if (state.jumping) return;

      let duration = 0;
      if (lastTick) {
        duration = timestamp - lastTick;
      }
      lastTick = timestamp;
      let frames = Math.floor(duration / MS_PER_FRAME) || 1;

      const newLocation = state.location + frames * TICK_SIZE * state.velocity;
      if (newLocation !== state.location) {
        state.location = newLocation;
      }

      if (
        state.acceleration === 0 ||
        Math.sign(state.acceleration) !== Math.sign(state.velocity)
      ) {
        // If the velocity isn't changing much, stop updating it. Performs
        // better for components watching location
        let newVelocity = state.velocity;

        while (frames > 0) {
          newVelocity = newVelocity * DECAY;
          frames--;
        }

        if (Math.abs(state.velocity - newVelocity) <= 0.000001) {
          state.velocity = 0;
        } else {
          state.velocity = newVelocity;
        }
      }
    },

    accelerate(state, direction) {
      state.velocity += direction * ACCELERATION_SPEED;
      if (state.velocity > VELOCITY_LIMIT) state.velocity = VELOCITY_LIMIT;
      if (state.velocity < -VELOCITY_LIMIT) state.velocity = -VELOCITY_LIMIT;

      if (direction !== 0) state.direction = direction;
      state.acceleration = direction;
    },

    setDirection(state, direction) {
      state.direction = direction;
    },

    sunsetGeojson(state, sunset) {
      state.sunset = sunset.features[0];
    },

    setLocation(state, location) {
      state.location = getCorrectedLocation(location);
      state.velocity = 0;
      state.acceleration = 0;
    },

    setFlip(state, { flip, demo }) {
      if (demo && !state.needsToDemoFlip) return;
      if (demo && state.jumping) return;
      if (demo) {
        state.needsToDemoFlip = flip;
      }
      state.flipped = flip;
      state.direction = flip ? 1 : -1;
    },

    resetTour(state) {
      state.needsToDemoFlip = true;
    },

    setJumping(state, jumping) {
      state.jumping = jumping;
    },

    setSearchResults(state, results) {
      state.searchResults = results;
    },

    setIntersections(state, intersections) {
      state.intersections = intersections;
    }
  },

  getters: {
    totalDistance(state) {
      if (!state.sunset) return {};
      let totalDistance = turf.length(state.sunset);
      return totalDistance;
    },
    currentLatLonZoom(state, getters) {
      if (!state.sunset) return {};

      let epsilon = 0.002;

      let point = turf.along(
        state.sunset,
        getCorrectedLocation(state.location) * getters.totalDistance
      );
      let pointPlusEpsilon =
        state.location < 1 - epsilon
          ? turf.along(
              state.sunset,
              getCorrectedLocation(state.location + epsilon) *
                getters.totalDistance
            )
          : point;
      let pointMinusEpsilon =
        state.location > epsilon
          ? turf.along(
              state.sunset,
              getCorrectedLocation(state.location - epsilon) *
                getters.totalDistance
            )
          : point;

      let bearing = turf.bearing(pointMinusEpsilon, pointPlusEpsilon) + 90;
      if (state.flipped) bearing += 180;

      let zoom =
        15.75 -
        (Math.abs(state.velocity * 0.001) > 0.00025
          ? (Math.abs(state.velocity * 0.001) - 0.00025) * 1000
          : 0);

      point = point.geometry.coordinates;

      if (isNaN(point[0])) point = [34, -117];

      return { bearing, point, zoom };
    }
  },

  actions: {
    loadSunsetGeojson({ commit }) {
      fetch(`${dataDir}/sunset.geojson`)
        .then(r => r.json())
        .then(sunset => {
          commit("sunsetGeojson", sunset);
        });

      fetch(`${dataDir}/intersections.json`)
        .then(r => r.json())
        .then(intersections => {
          commit("setIntersections", intersections);
        });
    },

    async geocode({ commit, state, getters }, { value }) {
      value = encodeURIComponent(value);

      let results = await fetch(
        "https://api.mapbox.com/geocoding/v5/mapbox.places/" +
          value +
          ".json?access_token=pk.eyJ1Ijoic3RhbWVuIiwiYSI6IlpkZEtuS1EifQ.jiH_c9ShtBwtqH9RdG40mw&limit=10&bbox=-118.708359,33.841330,-117.920090,34.253239"
      ).then(r => r.json());

      if (!results) results = [];

      results = results.features.map(f => {
        let point = turf.nearestPointOnLine(state.sunset, f.center);

        // Track the side of Sunset the search result is likely on, in case we
        // need to flip when navigating to it
        const closestLatitude = point.geometry.coordinates[1];
        const side = f.center[1] >= closestLatitude ? "north" : "south";

        return {
          ...f,
          closestPosition: point.properties.location / getters.totalDistance,
          distance: point.properties.dist,
          side
        };
      });

      commit("setSearchResults", results);
    },

    async goToLocation({ commit, state }, { target }) {
      commit("setJumping", true);

      let startLocation = state.location;

      if (target < startLocation) {
        commit("setDirection", -1);
      } else {
        commit("setDirection", 1);
      }

      let i = 0;
      let timer;

      timer = window.setInterval(() => {
        let location =
          startLocation * (1 - d3.easeSinInOut(i)) +
          target * d3.easeSinInOut(i);

        commit("setLocation", location);

        i += 0.02;

        if (i >= 1) {
          window.clearInterval(timer);

          commit("setLocation", target);
          commit("setJumping", false);
        }
      }, 30);
    },

    goDirectlyToLocation({ state }, { target }) {
      state.direction = target < state.location ? -1 : +1;
      state.location = getCorrectedLocation(target);
    },

    goToLngLat({ dispatch, getters, state }, { lngLat }) {
      const point = turf.point([lngLat.lng, lngLat.lat]);
      const nearestPoint = turf.nearestPointOnLine(state.sunset, point);
      const target = nearestPoint.properties.location / getters.totalDistance;
      dispatch("goDirectlyToLocation", { target });
    },

    flip({ commit, state }) {
      if (state.jumping) return;

      commit("setFlip", {
        flip: !state.flipped,
        pushToURL: { flip: !state.flipped }
      });
      commit("setDirection", state.direction * -1);
    }
  }
};

export default geolocation;
